import axios from 'axios';
import type { DateTime } from 'luxon';
import { z } from 'zod';

import { getHttpStatus } from '@/lib/api/errors';
import { zodTimeStamp } from '@/utils/zodUtils';

// The whole server contract for violation discounts lives in this module:
// routes, request bodies, response shapes and refusals. A rename on the
// server is a change to this file only.

export const violationDiscountPath = (violationId: string) =>
  `/monitor/violation/${violationId}/discount`;

export const locationDiscountOffersPath = (locationId: string) =>
  `/location/configs/${locationId}`;

// The reasons a new discount can be given with, in the order they are offered.
export const DISCOUNT_REASONS = [
  'keying_error_on_plate',
  'wrong_location_or_date',
  'payment_failed',
  'technology_issue',
  'validation_or_permit_issue',
  'exceeded_paid_time',
  'unaware_of_rules',
  'unclear_signage_or_guidance',
  'brief_visit',
  'first_offense_courtesy',
  'financial_hardship',
  'notice_late_or_not_received',
  'fee_or_due_date_confusion',
  'support_delay_or_system_error',
  'original_rate_requested',
  'operator_approved',
  'other',
] as const;

export type DiscountReason = (typeof DISCOUNT_REASONS)[number];

// No longer offered, but discounts already given with them stay in history.
export const RETIRED_DISCOUNT_REASONS = [
  'signage_or_payment_issue',
  'new_lot',
] as const;

export type RetiredDiscountReason = (typeof RETIRED_DISCOUNT_REASONS)[number];

export const isDiscountReason = (reason: string): reason is DiscountReason =>
  DISCOUNT_REASONS.some((known) => known === reason);

export const DISCOUNT_NOTE_MAX_LENGTH = 450;

export type DiscountEndReason = 'expired' | 'removed' | 'replaced';

// `reason` and `endReason` stay plain strings so a value added on the server
// later still parses and the history keeps loading.
const DiscountRecordSchema = z.object({
  id: z.string(),
  amountCents: z.number(),
  reason: z.string(),
  note: z.string().nullish(),
  appliedBy: z.string(),
  appliedAt: zodTimeStamp,
  expiresAt: zodTimeStamp,
  floorCents: z.number(),
  isBelowFloor: z.boolean(),
  endedAt: zodTimeStamp.nullish(),
  endedBy: z.string().nullish(),
  endReason: z.string().nullish(),
});

const ActiveDiscountSchema = DiscountRecordSchema.extend({
  currentSchedulePriceCents: z.number(),
});

const DiscountOffersSchema = z.object({
  firstOfferCents: z.number(),
  secondOfferCents: z.number(),
  floorCents: z.number(),
  isDefault: z.boolean(),
});

const PriceStepSchema = z.object({
  at: zodTimeStamp,
  priceCents: z.number(),
});

export const ViolationDiscountStateSchema = z.object({
  violationId: z.string(),
  currentPriceCents: z.number(),
  hasHearingOfficerFees: z.boolean(),
  active: ActiveDiscountSchema.nullable(),
  // Newest first: the server orders it, so the latest is at the front.
  history: z.array(DiscountRecordSchema),
  offers: DiscountOffersSchema,
  nextPriceStep: PriceStepSchema.nullable(),
  // Every schedule rise after now and no further ahead than a discount may
  // last. Absent from a server that does not send it yet, which is not the
  // same as an empty list: then only `nextPriceStep` is known.
  priceSteps: z.array(PriceStepSchema).optional(),
});

export type DiscountRecord = z.infer<typeof DiscountRecordSchema>;
export type ActiveDiscount = z.infer<typeof ActiveDiscountSchema>;
export type DiscountOffers = z.infer<typeof DiscountOffersSchema>;
export type PriceStep = z.infer<typeof PriceStepSchema>;
export type ViolationDiscountState = z.infer<
  typeof ViolationDiscountStateSchema
>;

export type ApplyViolationDiscountInput = {
  amountCents: number;
  reason: DiscountReason;
  note: string;
  expiresAt: DateTime<true>;
};

export const toApplyViolationDiscountBody = ({
  amountCents,
  reason,
  note,
  expiresAt,
}: ApplyViolationDiscountInput) => ({
  amountCents,
  reason,
  note: note.trim(),
  expiresAt: expiresAt.toUTC().toISO({ suppressMilliseconds: true }),
});

export type LocationDiscountOffersInput = {
  firstOfferCents: number;
  secondOfferCents: number;
  floorCents: number;
};

// The server merges enforcement key by key (`mergeEnforcementUpdate`): a key
// that is absent stays as it is. So a body carrying only
// `enforcement.discount_offers` leaves every other enforcement setting of the
// lot alone, and the Location Detail save, which never carries
// `discount_offers`, leaves these amounts alone.
export const toLocationDiscountOffersBody = (
  offers: LocationDiscountOffersInput
) => ({
  enforcement: {
    discount_offers: {
      first_offer_cents: offers.firstOfferCents,
      second_offer_cents: offers.secondOfferCents,
      floor_cents: offers.floorCents,
    },
  },
});

// Every refusal is one problem body: `code` names the cause and `detail` is
// the server's own sentence for it.
// A field that is empty or not a string says nothing, and one bad field does
// not discard the other.
const refusalText = z.string().trim().min(1).optional().catch(undefined);

const DiscountRefusalSchema = z.object({
  code: refusalText,
  detail: refusalText,
});

export type DiscountRefusal = z.infer<typeof DiscountRefusalSchema> & {
  status: number | undefined;
};

export function parseDiscountRefusal(error: unknown): DiscountRefusal {
  const parsed = DiscountRefusalSchema.safeParse(
    axios.isAxiosError(error) ? error.response?.data : undefined
  );
  return { status: getHttpStatus(error), ...(parsed.success && parsed.data) };
}
